    import React, { useState, useRef, useEffect } from 'react';
import ModalWrapper from '../../ModalWrapper';
import { DO_NOTHING } from '../../../utils/commonfunctions';
import { Box } from '@mui/material';
import { FormWrapper, CommonFooter } from '../../FormComponent';
import { FormSchemas } from '../../FormComponent/ValidationSchemas';

    const VARIABLE_TYPES = [
        { label: 'Environment Variables', value: 'environment_variable' },
        { label: 'Project Variables', value: 'project_variable' },
    ];

    export default function AddUpdateVariablePopup({
        closePopup = DO_NOTHING,
        handleVariableAdd = DO_NOTHING,
        handleVariableUpdate = DO_NOTHING,
        initialValues = {},
        type = 'add',
        loading = false,
    }) {
        const [isSecret, setIsSecret] = useState(
            typeof initialValues.isSecret === 'boolean' ? initialValues.isSecret : false
        );
        const [variableType, setVariableType] = useState(initialValues.type ?? 'project_variable');
        const formRef = useRef();

        // Handle variable type change
        useEffect(() => {
            if (formRef.current) {
                // Update the form value when type changes
                formRef.current.setValue('type', variableType);
                
                // If switching to environment variable, clear the value field
                if (variableType === 'environment_variable') {
                    formRef.current.setValue('value', '');
                }
            }
        }, [variableType]);

        // Update isSecret state when form value changes
        useEffect(() => {
            if (formRef.current) {
                formRef.current.setValue('isSecret', isSecret);
            }
        }, [isSecret]);

        // Handle form submission
        const handleFormSubmit = async () => {
            if (formRef.current) await formRef.current.submit();
        };

        const handleConfirmSubmit = async (formValues) => {
            const submitData = {
                ...formValues,
                type: variableType,
            };
            
            // For environment variables, ensure value is not included
            if (variableType === 'environment_variable') {
                delete submitData.value;
            }
            
            if (type === 'add') {
                handleVariableAdd(submitData);
            } else {
                handleVariableUpdate({
                    ...submitData,
                    id: initialValues?.id,
                });
            }
        };

        // Field definitions for FormWrapper
        const fields = [
            {
                name: 'key',
                type: 'text',
                label: 'Variable Key',
                required: true,
                autoFocus: true,
                disabled: type === 'edit',
            },
            // Only show value field for project variables
            ...(variableType === 'project_variable' ? [{
                name: 'value',
                type: 'text',
                label: 'Variable Value',
                required: true,
            }] : []),
            {
                name: 'description',
                type: 'text',
                label: 'Variable Description',
                required: false,
            },
            {
                name: 'type',
                type: 'select',
                label: 'Type',
                size: 'small',
                required: true,
                options: VARIABLE_TYPES,
                disabled: type === 'edit',
                onChange: (e) => setVariableType(e.target.value),
            },
            {
                name: 'isSecret',
                type: 'switch',
                label: 'Secret Variable',
                checked: isSecret,
                onChange: () => setIsSecret((prev) => !prev),
                disabled: type === 'edit',
            },
        ];

        // Buttons for CommonFooter
        const buttons = [
            {
                label: 'Cancel',
                variant: 'outline',
                className: 'primary',
                onClick: closePopup,
                disabled: loading,
            },
            {
                label: loading ? (type === 'add' ? 'Adding...' : 'Updating...') : (type === 'add' ? 'Add' : 'Update'),
                variant: 'primary',
                className: 'white bg-blue600',
                onClick: handleFormSubmit,
                disabled: loading,
            },
        ];

        return (
            <ModalWrapper
                isOpen={true}
                onClose={closePopup}
                title={type === 'add' ? 'Add New Variable' : 'Edit Variable'}
                size="small"
                footer={
                    <CommonFooter
                        buttons={buttons}
                        align="right"
                    />
                }
            >
                <FormWrapper
                    ref={formRef}
                    schema={FormSchemas.variableForm}
                    fields={fields}
                    onSubmit={handleConfirmSubmit}
                    defaultValues={{
                        key: initialValues?.key ?? '',
                        ...(variableType === 'project_variable' && { value: initialValues?.value ?? '' }),
                        description: initialValues?.description ?? '',
                        type: variableType,
                        isSecret: isSecret,
                    }}
                />
            </ModalWrapper>
        );
    }
